<script setup>
import InputText from 'primevue/inputtext'
import InputWrapper from '@/components/shared/form/InputWrapper.vue'
import Spinner from '@/components/shared/core/Spinner.vue'
import {useFocus} from '@/composables/use-focus.js'
import {useTextInsertion} from '@/composables/use-text-insertion.js'
import {toRefs, ref, watch, useTemplateRef} from 'vue'

//Emits and props
const emit = defineEmits([
  'click',
  'input',
  'keyup',
  'keydown',
  'dirty',
  'focus',
  'blur',
])
const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  value: {
    type: [String, Number],
    default: '',
  },
  selectOnFocus: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  inputClass: {
    type: String,
    default: '',
  },
})

//Get props
const {
  value,
  selectOnFocus,
} = toRefs(props)

//Input ref and value
const inputRef = useTemplateRef('inputRef')
const inputValue = ref(value.value || '')
const {insertText} = useTextInsertion(inputRef)

//Watch value
watch(value, () => {
  if (value.value === null || value.value === undefined) {
    inputValue.value = ''
  }
  else {
    inputValue.value = String(value.value)
  }
}, {immediate: true})

//Input field event handlers
const onInput = event => {
  const {value} = event.target
  emit('input', {value})
  emit('dirty', {value})
}
const onKeydown = event => emit('keydown', event)
const onKeyup = event => emit('keyup', event)
const onBlur = event => emit('blur', event)
const onFocus = event => {
  if (selectOnFocus.value) {
    event.target.select()
  }
  emit('focus', event)
}

//Expose focus and blur methods to allow parent components to focus/blur the input
const {focus, blur} = useFocus(inputRef)
defineExpose({
  insertText,
  focus,
  blur,
})
</script>

<template>
  <InputWrapper @click="$emit('click', $event)">
    <InputText
      ref="inputRef"
      v-bind="$attrs"
      :value="inputValue"
      :type="type"
      :disabled="isDisabled"
      :readonly="isReadonly"
      :invalid="hasError"
      size="large"
      fluid
      :class="{
        [$style.input]: true,
        [inputClass]: !!inputClass,
        [$style.withIconLeft]: $slots.iconLeft,
        [$style.withIconRight]: $slots.iconRight || isLoading,
      }"
      @input="onInput($event)"
      @keyup="onKeyup($event)"
      @keydown="onKeydown($event)"
      @focus="onFocus($event)"
      @blur="onBlur($event)"
    />

    <template #iconLeft>
      <slot name="iconLeft" />
    </template>

    <template #iconRight>
      <Spinner v-if="isLoading" />
      <slot
        v-else
        name="iconRight"
      />
    </template>

    <slot name="inputWrapper" />
  </InputWrapper>
</template>

<style module>
.input {
  &.withIconLeft {
    padding-left: 2.5rem;
  }
  &.withIconRight {
    padding-right: 2.5rem;
  }
  &::-webkit-search-cancel-button{
    display: none;
  }
}
</style>
